import { AInput } from "@/admin/form/Fields.tsx";
import {
    commitmentOfferLocationQueryAtom,
    commitmentOfferUpdateMutation,
} from "@/admin/metal/commitments/metalCommitmentOfferQuery.ts";
import { InfoTooltip } from "@/components/WTooltip.tsx";
import { WCheckbox } from "@/components/forms/WCheckbox.tsx";
import { WNumberField } from "@/components/forms/WNumberField.tsx";
import { CurrencyBalance, CurrencyMonthly } from "@/components/l10n/Currency.tsx";
import { WModalContent } from "@/components/modal/WModal.tsx";
import { WTable, WTableBody } from "@/components/table/WTable.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { useLocationsForType } from "@/modules/location/locationQuery.ts";
import { periodLabels } from "@/modules/metal/offer/period.tsx";
import { hoursPerMonth } from "@/modules/pricing/hourlyToMonthly.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import type { MetalCommitmentOffer, MetalCommitmentPeriodEnum } from "@warrenio/api-spec/spec.oats.gen";
import { type ReactNode, useState } from "react";

interface PeriodPriceRow {
    period_hours: MetalCommitmentPeriodEnum;
    title: ReactNode;
    price?: number;
    isActive?: boolean;
}

export function EditMetalOfferPricesModal({ uuid, price }: { uuid: string; price?: number }) {
    //#region Hooks
    const locations = useLocationsForType("bare_metal");
    const location = locations.defaultLocation;
    const metalSpecUuid = uuid.replace(/^metal_spec_uuid:/, "");
    const offers = useSuspenseQueryAtom(commitmentOfferLocationQueryAtom(location, metalSpecUuid));

    const updateMutation = useStandardMutation(commitmentOfferUpdateMutation);
    //#endregion

    const periodRows: PeriodPriceRow[] = Object.entries(periodLabels).map(([key, title]) => {
        const period_hours = Number(key) as MetalCommitmentPeriodEnum;
        const offer = offers.find((offer) => offer.period_hours === period_hours);

        return {
            period_hours,
            title,
            price: period_hours ? Number(offer?.price) : price,
            isActive: period_hours ? !!offer?.price : true,
        };
    });

    const [rows, setRows] = useState(periodRows);

    function updateRow(value: number, index: number) {
        setRows((prevRows) => {
            const updatedRows = [...prevRows];
            updatedRows[index] = { ...updatedRows[index], price: value };

            return updatedRows;
        });
    }

    function updateIsActive(value: boolean, index: number) {
        setRows((prevRows) => {
            const updatedRows = [...prevRows];
            updatedRows[index] = { ...updatedRows[index], isActive: value };

            return updatedRows;
        });
    }

    async function onSubmit() {
        const body: MetalCommitmentOffer[] = rows
            .filter(
                (row): row is PeriodPriceRow & { price: number } =>
                    row.isActive === true && row.price != null && !!row.period_hours,
            )
            .map(({ period_hours, price }) => ({
                period_hours,
                price: String(price),
            }));

        console.log("Updating offer with body: %o", body);
        await updateMutation.mutateAsync({ body, uuid: metalSpecUuid, location });
    }

    return (
        <WModalContent title="Edit Period Prices" label="Save" modalAction={onSubmit}>
            <WTable>
                <thead>
                    <tr>
                        <th>Billing cycle</th>
                        <th>Price per cycle</th>
                        <th>
                            Estimated Price per Month
                            <InfoTooltip
                                className="ml-1"
                                text="Monthly prices are estimates, 1 month is approximated to 730 hours."
                            />
                        </th>
                        <th>Enabled</th>
                    </tr>
                </thead>
                <WTableBody>
                    {rows.map((item, index) => {
                        const disabledClass = item.isActive ? "" : "opacity-50 pointer-events-none";

                        return (
                            <tr key={item.period_hours}>
                                <td className={disabledClass}>{item.title}</td>
                                <td className={disabledClass}>
                                    <WNumberField
                                        headless
                                        value={item.price}
                                        onChange={(value) => updateRow(value, index)}
                                        defaultValue={NaN}
                                        aria-label="Set price per period"
                                        isRequired
                                        isDisabled={!item.period_hours}
                                        formatOptions={{
                                            maximumFractionDigits: 10,
                                        }}
                                    >
                                        <AInput />
                                    </WNumberField>
                                </td>
                                <td className={disabledClass}>
                                    {item.price ? (
                                        item.period_hours ? (
                                            <CurrencyBalance value={item.price / (item.period_hours / hoursPerMonth)} />
                                        ) : (
                                            <CurrencyMonthly value={item.price} accurate={true} />
                                        )
                                    ) : (
                                        "-"
                                    )}
                                </td>
                                <td>
                                    <WCheckbox
                                        aria-label="Enable/disable"
                                        isSelected={item.isActive}
                                        isDisabled={!item.period_hours}
                                        onChange={(value) => updateIsActive(value, index)}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </WTableBody>
            </WTable>
        </WModalContent>
    );
}
